import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { resetSlicesStateThunk } from '../../../../main/slices/app-lifecycle/thunks/reset-slices-state.thunk.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsAuthUtil } from '../../../../main/utils/aws/aws-auth.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';

import { SignInInputType } from '../../../types/accounts-validation.types.ts';

import { setCurrentAccount } from '../../accounts/accounts.slice.ts';

import { setHasUserSignedIn, resetAuthFormData } from '../auth.slice.ts';
import { AUTH_SLICE_NAME, AuthSliceState } from '../auth.slice.types.ts';

import { identifyUserTrackersThunk } from './identify-user-trackers.thunk.ts';
import { postInitialAuthenticationThunk } from './post-initial-authentication.thunk.ts';

interface Props extends SignInInputType {
  skipLoginAnalytics?: boolean;
}

interface ReturnType {
  email: string;
  userId: string;
  preferredName?: string;
  studentId?: string;
}

export const signInThunk = createAsyncThunk<ReturnType | undefined, Props, ThunkApiConfigType>(
  `${AUTH_SLICE_NAME}/signInThunk`,
  async (props, thunkAPI) => {
    const { email, password, skipLoginAnalytics } = props;
    const isRememberMe = thunkAPI.getState().auth.isRememberMe;

    try {
      await getAwsAuthUtil().signIn(email, password, { isRememberMe });
      const currentUser = await getAwsAuthUtil().getCurrentUser();
      if (!currentUser) {
        return undefined;
      }

      thunkAPI.dispatch(resetSlicesStateThunk());

      thunkAPI.dispatch(
        identifyUserTrackersThunk({
          email: currentUser.email,
          userId: currentUser.userId,
          preferredName: currentUser.preferredName,
        })
      );

      if (!skipLoginAnalytics) {
        getTrackerUtil().track({
          event: TrackingEventEnum.PARENT_SIGN_IN,
          data: {
            parentEmail: currentUser.email,
            parentName: currentUser.preferredName,
            parentUUID: currentUser.userId,
          },
        });
      }

      thunkAPI.dispatch(setHasUserSignedIn(true));
      thunkAPI.dispatch(
        setCurrentAccount({
          email: currentUser.email,
          userId: currentUser.userId,
          preferredName: currentUser.preferredName ?? '',
          studentId: currentUser.studentId ?? '',
        })
      );

      await thunkAPI.dispatch(postInitialAuthenticationThunk()).unwrap();
      thunkAPI.dispatch(resetAuthFormData());

      return currentUser;
    } catch (error: unknown) {
      getToasterUtil().error((error as Error)?.message ?? getLocaleUtil().select('auth:sign_in_failed'));
      return undefined;
    }
  }
);

export const signInThunkExtraReducers = (builder: ActionReducerMapBuilder<AuthSliceState>): void => {
  builder.addCase(signInThunk.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(signInThunk.fulfilled, (state) => {
    state.isLoading = false;
  });
};
